/**
 * The calendar for date fields (parts/forms/field.php), styled like the booking calendar
 * (checkout/components/CheckoutCalendarModal.vue). The field is a button that opens it, and the chosen
 * date goes in the hidden field next to it as Y-m-d, which is what the server expects. Weeks start on
 * Monday and past dates can't be chosen. One tap picks a date and closes it; arrow keys move between
 * days, Page Up and Down between months, and Escape closes it
 */

// Drawn rather than typed (‹ ›), because text arrows sit low in their circles
const chevron = (path) => `<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" focusable="false"><path d="${path}" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/></svg>`

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"]

// Where each key moves the focused day to; Shift with Page Up or Down moves a year
const MOVES = {
  ArrowLeft: (date) => addDays(date, -1),
  ArrowRight: (date) => addDays(date, 1),
  ArrowUp: (date) => addDays(date, -7),
  ArrowDown: (date) => addDays(date, 7),
  Home: (date) => addDays(date, -weekday(date)),
  End: (date) => addDays(date, 6 - weekday(date)),
  PageUp: (date, shift) => addMonths(date, shift ? -12 : -1),
  PageDown: (date, shift) => addMonths(date, shift ? 12 : 1),
}

/**
 * The calendar that's open, if any: its .cf-date wrapper, field button, hidden field, calendar, and the
 * day that has (or will get) keyboard focus. The month shown is always that day's month
 */
let current = null

export const listenForDatePickers = () => {
  document.addEventListener("click", (event) => {
    const button = event.target instanceof Element ? event.target.closest(".cf-date-button") : null
    if (button) {
      if (current?.button === button) {
        close(true)
      } else {
        open(button)
      }
    }
  })

  // Down arrow opens it too, as it does for dropdowns, or goes into it when it's open
  document.addEventListener("keydown", (event) => {
    if (event.key === "ArrowDown" && event.target instanceof Element && event.target.matches(".cf-date-button")) {
      event.preventDefault()
      if (current?.button === event.target) {
        focusDay()
      } else {
        open(event.target)
      }
    }
  })

  // A tap or click anywhere else closes it
  document.addEventListener("pointerdown", (event) => {
    if (current && !current.picker.contains(event.target)) {
      close()
    }
  })
}

const open = (button) => {
  close()

  const picker = button.closest(".cf-date")
  const input = picker.querySelector("input[type=hidden]")
  const calendar = calendarFor(picker, button)
  const now = today()
  const chosen = parse(input.value)

  current = { picker, button, input, calendar, focus: chosen && chosen >= now ? chosen : now }
  render()

  calendar.hidden = false
  button.setAttribute("aria-expanded", "true")
  calendar.scrollIntoView({ block: "nearest" })
  focusDay()
}

const close = (returnFocus = false) => {
  if (!current) {
    return
  }

  const { button, calendar } = current
  current = null
  calendar.hidden = true
  button.setAttribute("aria-expanded", "false")

  if (returnFocus) {
    button.focus()
  }
}

/**
 * Put the date in the hidden field and show it on the button. null clears it
 */
const choose = (date) => {
  const { button, input } = current
  const value = date ? ymd(date) : ""

  input.value = value
  button.value = value
  button.classList.toggle("has-date", !!date)
  button.querySelector(".cf-date-text").textContent = date ? `${short(date)} ${date.getFullYear()}` : "Choose a date"

  close(true)
  // So the form checks the answer again, as it does for typed ones (forms.js)
  button.dispatchEvent(new Event("change", { bubbles: true }))
}

/**
 * The calendar is made the first time it's opened, so pages carry no extra markup
 */
const calendarFor = (picker, button) => {
  let calendar = picker.querySelector(".cf-calendar")

  if (calendar) {
    return calendar
  }

  const id = `${button.id}-calendar`
  calendar = document.createElement("div")
  calendar.className = "cf-calendar"
  calendar.id = id
  calendar.hidden = true
  calendar.setAttribute("role", "dialog")
  calendar.setAttribute("aria-label", "Choose a date")
  // Safari doesn't focus buttons when they're clicked, so focus lands here instead of leaving the picker
  calendar.tabIndex = -1
  calendar.innerHTML = `
    <div class="cf-calendar-head">
      <button type="button" class="cf-calendar-nav" data-months="-1" aria-label="Previous month">${chevron("M10.5 3 5.5 8l5 5")}</button>
      <p class="cf-calendar-title" id="${id}-title" aria-live="polite"></p>
      <button type="button" class="cf-calendar-nav cf-calendar-nav--next" data-months="1" aria-label="Next month">${chevron("M5.5 3l5 5-5 5")}</button>
    </div>
    <table class="cf-calendar-grid" role="grid" aria-labelledby="${id}-title">
      <thead><tr>${DAYS.map((day) => `<th scope="col" abbr="${day}">${day.slice(0, 3)}</th>`).join("")}</tr></thead>
      <tbody></tbody>
    </table>
    <div class="cf-calendar-foot">
      <button type="button" class="cf-calendar-clear">Clear</button>
      <p class="cf-calendar-today"></p>
    </div>`

  button.after(calendar)
  button.setAttribute("aria-controls", id)

  calendar.addEventListener("click", (event) => {
    const target = event.target.closest("button")

    if (!target || !current) {
      return
    }

    if (target.dataset.date) {
      choose(parse(target.dataset.date))
    } else if (target.dataset.months) {
      current.focus = latest(addMonths(current.focus, +target.dataset.months), today())
      render()
    } else if (target.matches(".cf-calendar-clear")) {
      choose(null)
    }
  })

  picker.addEventListener("keydown", (event) => {
    if (!current || current.picker !== picker) {
      return
    }

    if (event.key === "Escape") {
      event.preventDefault()
      event.stopPropagation()
      close(true)
      return
    }

    const move = MOVES[event.key]
    if (!move || !event.target.matches(".cf-calendar-day")) {
      return
    }

    event.preventDefault()
    const next = latest(move(current.focus, event.shiftKey), today())
    const otherMonth = next.getMonth() !== current.focus.getMonth() || next.getFullYear() !== current.focus.getFullYear()
    current.focus = next

    if (otherMonth) {
      render()
    } else {
      calendar.querySelectorAll(".cf-calendar-day").forEach((day) => {
        day.tabIndex = day.dataset.date === ymd(next) ? 0 : -1
      })
    }
    focusDay()
  })

  // Tabbing out of it closes it. Focus going nowhere (Safari clicks, a day being redrawn) doesn't
  picker.addEventListener("focusout", (event) => {
    if (current?.picker === picker && event.relatedTarget && !picker.contains(event.relatedTarget)) {
      close()
    }
  })

  return calendar
}

/**
 * Draw the month of current.focus: whole weeks, with the other months' days shown faintly
 */
const render = () => {
  const { calendar, focus, input } = current
  const now = today()
  const first = new Date(focus.getFullYear(), focus.getMonth(), 1)
  const last = new Date(focus.getFullYear(), focus.getMonth() + 1, 0)

  calendar.querySelector(".cf-calendar-title").textContent = `${MONTHS[first.getMonth()]} ${first.getFullYear()}`
  calendar.querySelector(".cf-calendar-today").textContent = `Today is ${short(now)}`

  // There's nothing to choose before this month. Disabling a focused button drops focus, so move it on
  const previous = calendar.querySelector("[data-months='-1']")
  const hadFocus = document.activeElement === previous
  previous.disabled = first <= now
  if (previous.disabled && hadFocus) {
    calendar.querySelector("[data-months='1']").focus()
  }

  const tbody = calendar.querySelector("tbody")
  tbody.replaceChildren()

  for (let date = addDays(first, -weekday(first)); date <= last; ) {
    const row = tbody.insertRow()

    for (let i = 0; i < 7; i++, date = addDays(date, 1)) {
      const cell = row.insertCell()
      const day = document.createElement("button")
      const value = ymd(date)
      const outside = date.getMonth() !== first.getMonth()

      day.type = "button"
      day.className = outside ? "cf-calendar-day is-outside" : "cf-calendar-day"
      day.textContent = date.getDate()
      day.dataset.date = value
      day.tabIndex = value === ymd(focus) ? 0 : -1
      day.disabled = outside || date < now
      day.setAttribute("aria-label", `${DAYS[weekday(date)]} ${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`)

      if (+date === +now) {
        day.setAttribute("aria-current", "date")
      }
      if (value === input.value) {
        cell.setAttribute("aria-selected", "true")
      }

      cell.append(day)
    }
  }
}

const focusDay = () => {
  current?.calendar.querySelector(`[data-date="${ymd(current.focus)}"]`)?.focus({ preventScroll: true })
}

// Dates are local midnights, so comparing them compares days

const today = () => {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

const addDays = (date, days) => new Date(date.getFullYear(), date.getMonth(), date.getDate() + days)

// The same day in another month, or that month's last day when it's shorter
const addMonths = (date, months) => {
  const lastDay = new Date(date.getFullYear(), date.getMonth() + months + 1, 0).getDate()
  return new Date(date.getFullYear(), date.getMonth() + months, Math.min(date.getDate(), lastDay))
}

const latest = (a, b) => (a > b ? a : b)

// 0 for Monday to 6 for Sunday
const weekday = (date) => (date.getDay() + 6) % 7

const ymd = (date) => [date.getFullYear(), date.getMonth() + 1, date.getDate()].map((part) => String(part).padStart(2, "0")).join("-")

const parse = (value) => {
  const [, year, month, day] = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value || "") || []
  return year ? new Date(+year, month - 1, +day) : null
}

// "Mon 16 Nov"
const short = (date) => `${DAYS[weekday(date)].slice(0, 3)} ${date.getDate()} ${MONTHS[date.getMonth()].slice(0, 3)}`
